import { useState, useEffect } from 'react';
import AdminSidebar from '../../components/feature/AdminSidebar';
import { supabase } from '../../lib/supabase';

interface Transaction {
  id: string;
  type: 'subscription' | 'tip' | 'charge' | 'withdrawal';
  from: string;
  to: string;
  amount: number;
  date: string;
  status: 'completed' | 'pending' | 'failed';
}

export default function AdminTransactions() {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          from_profile:profiles!transactions_from_user_id_fkey(full_name, username),
          to_profile:profiles!transactions_to_user_id_fkey(full_name, username)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        const formattedTransactions = data.map((t: any) => ({
          id: t.id,
          type: t.type,
          from: t.from_profile?.full_name || t.from_profile?.username || '시스템',
          to: t.to_profile?.full_name || t.to_profile?.username || '시스템',
          amount: t.amount,
          date: new Date(t.created_at).toLocaleString('ko-KR'),
          status: t.status,
        }));
        setTransactions(formattedTransactions);
      }
    } catch (error) {
      console.error('거래 내역 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.from.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         transaction.to.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || transaction.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'subscription': return 'ri-vip-crown-line';
      case 'tip': return 'ri-gift-line';
      case 'charge': return 'ri-wallet-line';
      case 'withdrawal': return 'ri-bank-card-line';
      default: return 'ri-exchange-line';
    }
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'subscription': return '구독';
      case 'tip': return '팁';
      case 'charge': return '충전';
      case 'withdrawal': return '출금';
      default: return '거래';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-teal-100 text-teal-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return '완료';
      case 'pending': return '대기';
      case 'failed': return '실패';
      default: return status;
    }
  };

  const totalAmount = filteredTransactions
    .filter(t => t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);

  const subscriptionCount = transactions.filter(t => t.type === 'subscription').length;
  const tipCount = transactions.filter(t => t.type === 'tip').length;
  const pendingCount = transactions.filter(t => t.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50">
      <AdminSidebar />
      
      <div className="lg:ml-64 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* 헤더 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">거래 내역 관리</h1>
            <p className="text-gray-600">총 {transactions.length}건의 거래가 있습니다</p>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-400 rounded-lg flex items-center justify-center">
                  <i className="ri-exchange-line text-white text-lg"></i>
                </div>
                <p className="text-sm text-gray-600">총 거래액</p>
              </div>
              <p className="text-2xl font-bold text-gray-800">₩{totalAmount.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
                  <i className="ri-vip-crown-line text-white text-lg"></i>
                </div>
                <p className="text-sm text-gray-600">구독</p>
              </div>
              <p className="text-2xl font-bold text-gray-800">{subscriptionCount}건</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-red-400 rounded-lg flex items-center justify-center">
                  <i className="ri-gift-line text-white text-lg"></i>
                </div>
                <p className="text-sm text-gray-600">팁</p>
              </div>
              <p className="text-2xl font-bold text-gray-800">{tipCount}건</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-white text-lg"></i>
                </div>
                <p className="text-sm text-gray-600">대기 중</p>
              </div>
              <p className="text-2xl font-bold text-gray-800">{pendingCount}건</p>
            </div>
          </div>

          {/* 검색 및 필터 */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="거래 검색..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                />
              </div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="all">전체 유형</option>
                <option value="subscription">구독</option>
                <option value="tip">팁</option>
                <option value="charge">충전</option>
                <option value="withdrawal">출금</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="all">전체 상태</option>
                <option value="completed">완료</option>
                <option value="pending">대기</option>
                <option value="failed">실패</option>
              </select>
            </div>
          </div>

          {/* 거래 테이블 */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-teal-50 to-blue-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">유형</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">보낸 사람</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">받는 사람</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">금액</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">날짜</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        {searchTerm || typeFilter !== 'all' || statusFilter !== 'all' ? '검색 결과가 없습니다' : '아직 거래 내역이 없습니다'}
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50 transition-all">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-teal-100 to-blue-100 rounded-lg flex items-center justify-center">
                              <i className={`${getTransactionIcon(transaction.type)} text-teal-600 text-sm`}></i>
                            </div>
                            <span className="text-sm font-semibold text-gray-800">{getTransactionLabel(transaction.type)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-800">{transaction.from}</td>
                        <td className="px-6 py-4 text-sm text-gray-800">{transaction.to}</td>
                        <td className="px-6 py-4 text-sm font-bold text-gray-800">₩{transaction.amount.toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{transaction.date}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                            {getStatusLabel(transaction.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}